/**
 * The visit: a reader's stay on the site in one tab, from arriving from outside
 * until they leave or close it. Moving between the site's own pages or
 * languages stays within the visit. The Intro plays once per visit — on every
 * arrival from outside — and is skipped on same-site arrivals at the home page,
 * which open settled on the section the reader was on.
 *
 * The signal travels in the URL, once, with no on-device storage. A link to the
 * home page gains `?from=<section-id>` at click time, set by JavaScript, so the
 * static HTML and script-free navigation are untouched: without a script the
 * plain link works and the intro plays as today. The home page reads the marker
 * on arrival and strips it straight away.
 */

/** The query marker a same-site link to the home page carries. */
const FROM = 'from'

/** Adds the `from` marker to a home-page URL, naming the section the reader is on. */
export function markUrl(href: string, section: string): string {
  const url = new URL(href)
  url.searchParams.set(FROM, section)
  return url.href
}

/**
 * Reads and strips the `from` marker from a URL. Returns the carried section id
 * (or null when there is none) and the URL with the marker removed.
 */
export function stripMarker(href: string): { from: string | null; url: string } {
  const url = new URL(href)
  const from = url.searchParams.get(FROM)
  if (from !== null) url.searchParams.delete(FROM)
  return { from, url: url.href }
}

/** The id of the outline section the reader is on, read from the live outline mark; the hero by default. */
export function currentSection(root: ParentNode = document): string {
  const marked = root.querySelector<HTMLAnchorElement>('.outline a.on')
  return marked?.hash.slice(1) || 'about'
}

/**
 * Marks links to the home page. At click time each link's href gains
 * `?from=<section>`, so the same-site arrival skips the intro and opens on the
 * section the reader was on. Without JavaScript the plain href is followed.
 */
export function markHomeLinks(links: Iterable<HTMLAnchorElement>, section = currentSection) {
  for (const a of links) {
    a.addEventListener('click', () => {
      a.href = markUrl(a.href, section())
    })
  }
}

/**
 * Reads the `from` marker off the page's URL and strips it with
 * `history.replaceState` before anything else runs. Returns the carried
 * section's id, or null on an arrival from outside.
 */
export function readMarker(): string | null {
  const { from, url } = stripMarker(location.href)
  if (from !== null) history.replaceState(history.state, '', url)
  return from
}

/**
 * The section a carried `from` id names, if it is a real outline section (one
 * that carries `data-nav`: the hero, a stop, the services or the contact);
 * otherwise null, so a crafted or stale id carries nothing.
 */
export function carriedSection(
  from: string | null,
  root: ParentNode = document
): HTMLElement | null {
  if (!from) return null
  return root.querySelector<HTMLElement>(`#${CSS.escape(from)}[data-nav]`)
}
